import React, { useState, useEffect, useContext } from "react";
import "./Complaint.css";
import { useTranslation } from "react-i18next";
import { _t, getCookie } from "../../../../functions/Functions";
import { Helmet } from "react-helmet";
import ComplaintsSidebar from "./ComplaintsSidebar";
import { UserContext } from "../../../../contexts/User";
import { AuditContext } from "../../../../contexts/Audit";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import { ToastSuccess, ToastError } from "../../../../functions/ToastShow";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";
import Select from "react-select";
import moment from "moment";

export default function ComplaintTickets() {
  const { t } = useTranslation();
  const history = useHistory();
  const { authUserInfo, adminStaffForSearch } = useContext(UserContext);
  const { loading } = useContext(AuditContext);
  const { id } = useParams();

  const [ticketData, setTicketData] = useState({
    ticket_no: "",
    table_no: "",
    customer_name: "",
    complaint_date: "",
    full_history: null,
    guest_record: null
  });
  const [fetching, setFetching] = useState(false);
  
  const [remark, setRemark] = useState("");
  const [assignTo, setAssignTo] = useState(null);

  useEffect(() => {
    if (id) {
      fetchTicketData()
    }
  }, [id]);

  const fetchTicketData = async () => {
    setFetching(true);
    try {
      const response = await axios.get(`${BASE_URL}/complaints/${id}/follow-up`, {
        headers: { Authorization: `Bearer ${getCookie()}` }
      });
      const data = response.data;
      if (data) {
        setTicketData(data);
        if (data.assigned_to) {
          setAssignTo({
            id: data.assigned_to,
            name: data.assigned_to_name || "Assigned User"
          });
        }
      }
    } catch (error) {
      console.error("Error fetching ticket data", error);
    } finally {
      setFetching(false);
    }
  };

  const handleAddRemark = async () => {
    if (!remark.trim()) {
      ToastError({ title: _t(t("Please enter a remark")) });
      return;
    }
    if (!assignTo) {
      ToastError({ title: _t(t("Please assign the ticket to a user")) });
      return;
    }

    try {
      const payload = {
        remark: remark,
        assigned_to: assignTo?.id ?? null
      };

      await axios.post(`${BASE_URL}/complaints/${id}/follow-up`, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` }
      });

      setRemark("");
      ToastSuccess({ title: _t(t("Follow up added successfully")) });
      history.push("/dashboard/complaints/tickets-list");
      // Re-fetch data to get updated history
      fetchTicketData();
    } catch (error) {
      console.error("Error adding remark", error);
      ToastError({ title: _t(t("Failed to add follow-up")) });
    }
  };

  const handleEscalate = () => {
    const timeFormatted = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (ticketData) {
      setTicketData((prev) => ({
        ...prev,
        full_history: [
          ...(prev?.full_history || []),
          {
            id: Date.now(),
            time: timeFormatted,
            pip: "critical",
            text: "Escalated to Branch Manager — priority bumped for rapid resolution."
          }
        ]
      }));
    }
    ToastSuccess(_t(t("Ticket escalated to manager")));
  };

  return (
    <>
      <Helmet>
        <title>{_t(t("Ticket Detail"))}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* Left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ComplaintsSidebar />
            </div>

            {/* Main Content */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <CustomSpinLoader loading={loading || fetching || authUserInfo.permissions === null} />
              <div className="fk-scroll--pos-menu table-bottom-info-hide" data-simplebar>
                <div className="board-container">
                  {/* Top Header */}
                  <div className="board-header">
                    <div className="board-title">
                      <h1>{_t(t("Ticket detail"))}</h1>
                      <p>{_t(t("Full history and follow-up"))}</p>
                    </div>
                  </div>

                  {/* Full Layout */}
                  <div className="ticket-layout-full">
                    {/* Ticket Banner Card */}
                    <div className="ticket-card ticket-banner-card">
                      <div>
                        <div className="ticket-banner-title">
                          {ticketData?.ticket_no || ""}
                        </div>
                        <div className="ticket-banner-meta">
                          {[ticketData?.table_no ? `Table ${ticketData.table_no}` : null, ticketData?.customer_name].filter(Boolean).join(" · ") || "No guest details"}
                        </div>
                      </div>
                      <div className="ticket-banner-sla">
                        <div className="ticket-sla-date">
                          {ticketData?.complaint_date ? moment(ticketData.complaint_date).format('DD MMM YYYY, hh:mm A') : ""}
                        </div>
                        {/* <div className="ticket-sla-timer">06 : 15</div>
                        <div className="ticket-sla-status">{_t(t("SLA 5 min — breached"))}</div> */}
                      </div>
                    </div>

                    {/* Add Follow-Up Card */}
                    <div className="ticket-card">
                      <div className="ticket-section-title">{_t(t("Add a follow-up"))}</div>
                      <div className="ticket-section-subtext">
                        {_t(t("Every remark is locked with a timestamp and name. Past remarks can't be edited — only new ones added."))}
                      </div>

                      <div>
                        <textarea
                          className="ticket-textarea"
                          rows={3}
                          placeholder={_t(t("Remark"))}
                          value={remark}
                          onChange={(e) => setRemark(e.target.value)}
                        />
                      </div>

                      <div className="row mb-3">

                        <div className="ticket-control-group col-4">
                          <label className="ticket-control-label">{_t(t("Assign to"))} <span className="text-danger">*</span></label>
                          <Select
                            classNamePrefix="select"
                            getOptionLabel={(option)=> option.name}
                            getOptionValue={(option)=> option.id}
                            options={adminStaffForSearch ? adminStaffForSearch : []}
                            value={assignTo}
                            onChange={(selected) => setAssignTo(selected)}
                            placeholder={_t(t("Select Assignee"))}
                            isClearable
                          />
                        </div>
                        <div className="col-4"></div>
                        <div className="col-4 d-flex align-items-end justify-content-end">
                          <button type="button" className="ticket-btn-add" onClick={handleAddRemark}>
                            {_t(t("Add"))}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Bottom 2-Column Grid: Full History & This Guest's Record */}
                    <div className="ticket-bottom-grid">
                      {/* Left: Full History */}
                      <div className="ticket-card">
                        <div className="ticket-section-title">{_t(t("Full history"))}</div>
                        <div className="ticket-history-list">
                          {ticketData?.full_history?.length > 0 && ticketData?.full_history?.map((item, idx) => (
                            <div className="ticket-history-item" key={idx}>
                              <div className="ticket-history-time">{item.time}</div>
                              <div className="ticket-history-dot" style={{ backgroundColor: item.type === "closed" ? "#ef4444" : "#0a8d6e" }} />
                              <div className="ticket-history-content">
                                {item.type && <strong className="text-capitalize">{item.type} - </strong>}
                                {item.text}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Right: This Guest's Record */}
                      <div className="ticket-card">
                        <div className="ticket-section-title mb-3">{_t(t("This guest's record"))}</div>
                        <div className="ticket-guest-table">
                          <div className="ticket-guest-row">
                            <span className="ticket-guest-label">{_t(t("Name"))}</span>
                            <span className="ticket-guest-value">{ticketData?.guest_record?.name || "-"}</span>
                          </div>
                          <div className="ticket-guest-row">
                            <span className="ticket-guest-label">{_t(t("Visits"))}</span>
                            <span className="ticket-guest-value">{ticketData?.guest_record?.visits || "0"}</span>
                          </div>
                          <div className="ticket-guest-row">
                            <span className="ticket-guest-label">{_t(t("Lifetime spend"))}</span>
                            <span className="ticket-guest-value">{ticketData?.guest_record?.lifetime_spend || "0"}</span>
                          </div>
                          <div className="ticket-guest-row">
                            <span className="ticket-guest-label">{_t(t("First complaint?"))}</span>
                            <span className="ticket-guest-value highlight-warning">
                              {ticketData?.guest_record?.first_complaint || "-"}
                            </span>
                          </div>
                        </div>

                        {!!ticketData?.guest_record?.note && (
                          <div className="ticket-guest-suggestion">
                            <p className="mb-0">{ticketData.guest_record.note}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
